/* eslint-disable max-len */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    SettingControlType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';
export default class CompoundConditionNode extends BaseNode {
    operator: string;

    constructor() {
        super();
        this.linkedFeature = 'general';
        this.name = 'IF Compound Condition';
        this.icon = 'TbArrowsSplit2';
        this.id = 'node_compound_condition';

        const settings: Setting[] = [
            {
                id: 'EXPRESSIONS',
                name: 'Expressions',
                settingType: SettingType.ARRAY,
                controlType: SettingControlType.EXPRESSIONS,
                refId: 'condition_expression',
                required: true,
                default: [] as BaseFeature[],
            },
            {
                id: 'OPERATOR',
                name: 'Operator',
                settingType: SettingType.OPTION,
                required: true,
                options: [
                    {
                        name: 'AND',
                        value: 'AND',
                    },
                    {
                        name: 'OR',
                        value: 'OR',
                    },
                ],
                default: 'AND',
            },
        ];

        this.inputs = [
            {
                name: 'Condition',
                portId: 'cond',
                params: [],
            },
        ];

        this.outputs = [
            {
                name: 'True',
                portId: 'true_output',
                params: [],
            },
            {
                name: 'False',
                portId: 'false_output',
                params: [],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingValue(settingId: string) {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const EXPRESSIONS: BaseFeature[] = this.getSettingValue('EXPRESSIONS');
            let code = '';
            for (let i = 0; i < EXPRESSIONS.length; i++) {
                const expression: BaseFeature = EXPRESSIONS[i];
                const leftHandOperand = expression.getSettingValue('LEFT_OPERAND');
                const operator = expression.getSettingValue('OPERATOR');
                const rightHandOperand = expression.getSettingValue('RIGHT_OPERAND');
                code += `${leftHandOperand} ${operator} ${rightHandOperand}`;
                if (i !== EXPRESSIONS.length - 1) {
                    code += `<br> ${this.getSettingValue('OPERATOR')} `;
                }
            }
            return `IF ${code}`;
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const EXPRESSIONS = this.getSettingValue('EXPRESSIONS');
        let code = '';
        for (let i = 0; i < EXPRESSIONS.length; i++) {
            const expression: BaseFeature = EXPRESSIONS[i];
            const leftHandOperand = expression.getSettingValue('LEFT_OPERAND');
            const operator = expression.getSettingValue('OPERATOR');
            const rightHandOperand = expression.getSettingValue('RIGHT_OPERAND');
            code += `${leftHandOperand} ${operator} ${rightHandOperand}`;
            if (i !== EXPRESSIONS.length - 1) {
                code += `<br> ${this.getSettingValue('OPERATOR')} `;
            }
        }
        return `if ${code}`;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const EXPRESSIONS = this.getSettingValue('EXPRESSIONS');
        for (let i = 0; i < EXPRESSIONS.length; i++) {
            const expression: BaseFeature = EXPRESSIONS[i];
            const leftHandOperand = expression.getSettingValue('LEFT_OPERAND');
            const rightHandOperand = expression.getSettingValue('RIGHT_OPERAND');
            if (leftHandOperand === '' || rightHandOperand === '') {
                errors.push({
                    featureId: this.getID(),
                    settingId: 'EXPRESSIONS',
                    message: 'Condition expressions must not be empty.',
                    messageType: ValidationMessageType.ERROR,
                });
            }
        }
        return errors;
    }

    getDetails(): string {
        return `#### Condition (Advanced)
Similar to the Condtion block, this block can be used to combine multiple conditions
`;
    }
}
